import React, {useState, useEffect} from 'react'
import {toast} from 'react-toastify'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'

import MetaTags from '../../../../meta/MetaTags'
import DataTableList from '../../components/DataTableList'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {DOCTORS_BILLING_ENDING_SOON_HEADERS} from '../../../../constants/RoleConstants'
import { useBillingDropdown } from '../../context/BillingDropdownContext'
import { useReduxAuth } from '../../../auth'
import { useMADoctorDropdown } from '../../context/MADoctorDropdownContext'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

type ServerDataMinimalType = {billingCycles: Array<{cycle_end: number}>}

const BillingCycleEndingReport: React.FC = () => {
  const [patients, setPatients] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const { currentUser } = useReduxAuth()
  const { selectedDoctorId } = useBillingDropdown()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; full_name: string}>>([])

  // Fetch all doctors for MA
  useEffect(() => {
    if (roleId === USER_ROLES.MEDICAL_ASSISTANT) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            setMaSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          setMaDoctors([])
        })
    }
  }, [roleId, maSelectedDoctorId, setMaSelectedDoctorId])
  
  useEffect(() => {

    if (!selectedDoctorId && currentUser.role_id === USER_ROLES.BILLING_PERSONNEL) {
      // If no doctor is selected and user is billing personnel, clear patients and stop loading
      setPatients([])
      setLoading(false)
      return
    }

    if (!maSelectedDoctorId && currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      // If no doctor is selected and user is MA, clear patients and stop loading
      setPatients([])
      setLoading(false)
      return
    }

    setLoading(true)
    
    // Build query params based on role
    const params: { doctor_id?: number } = {}
    if (currentUser.role_id === USER_ROLES.BILLING_PERSONNEL) {
      params.doctor_id = selectedDoctorId
    } else if (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      params.doctor_id = maSelectedDoctorId
    }

    axios
      .get(API_ENDPOINTS.GET_BILLING_CYCLE_ENDING_REPORT(), {
        params
      })
      .then((resp) => {
        // Sort by default by cycle ending date
        let patientsData = resp.data.patients.sort(
          (a: ServerDataMinimalType, b: ServerDataMinimalType) =>
            a.billingCycles[0].cycle_end > b.billingCycles[0].cycle_end ? 1 : 0
        )

        // transform data to reuse the Table component
        patientsData = patientsData.map((p: ServerDataMinimalType) => {
          return {...p.billingCycles[0], ...p}
        })

        setPatients(patientsData)
      })
      .catch((err) => {
        console.error(
          '🔥 [BillingReport] Error fetching patients:',
          err.response?.status,
          err.response?.data?.message
        )
        // Clear doctors on error
        setPatients([])
        toast.error('Failed to load ending soon patients!')
      })
      .finally(() => setLoading(false))
  }, [selectedDoctorId, maSelectedDoctorId, currentUser.role_id])

  const exportToCSV = () => {}

  return (
    <>
      <MetaTags title='Cycle Ending Soon' />

      <div className='mb-4'>
        <h1 className='mb-2'>Cycle Ending Soon</h1>
        <p className='text-muted mb-0'>
          Lists patients whose billing cycle is ending in the next 10 days. Focus on their care, billing accuracy, and documentation for reimbursement.
        </p>
      </div>

      {/* Doctor Dropdown for MA */}
      {roleId === USER_ROLES.MEDICAL_ASSISTANT && (
        <div className='d-flex justify-content-end mb-3'>
          <div className='w-25'>
            <label htmlFor='doctorSelect' className='form-label'>
              Select Doctor
            </label>
            <select
              id='doctorSelect'
              className='form-select'
              value={maSelectedDoctorId ?? ''}
              onChange={(e) => {
                const selectedId = e.target.value ? parseInt(e.target.value) : undefined
                setMaSelectedDoctorId(selectedId)
              }}
              disabled={maDoctors.length === 0}
            >
            {maDoctors.length === 0 ? (
              <option value=''>-- No doctors available --</option>
            ) : (
              <>
                <option value=''>-- Select Doctor --</option>
                {maDoctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.full_name}
                  </option>
                ))}
              </>
            )}
            </select>
          </div>
        </div>
      )}

      {roleId === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId ? (
        <div className='card'>
          <div className='card-body text-center py-10'>
            <p className='text-muted'>Please select a doctor to view cycle ending soon patients.</p>
          </div>
        </div>
      ) : (
        <DataTableList
          title=''
          columns={DOCTORS_BILLING_ENDING_SOON_HEADERS}
          data={patients}
          isLoading={loading}
          showAddButton={false}
          showExportButton={false}
          onExportClick={exportToCSV}
          // handleStatusToggle={handleStatusToggle}
          allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]}
        />
      )}
    </>
  )
}

export default BillingCycleEndingReport
